import { useMemo } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ScheduleTypes } from '@frontend/api-schedule';
import type { AllDayCalendarEvent, CalendarColumnGroup, ProviderCalendarColumn } from '@frontend/calendar-grid';
import { getFullName } from '@frontend/name';
import { useAppScopeStore } from '@frontend/scope';
import { GridCalendarEvent } from '../components/calendar-view/events-cards/grid-event-card-switch';
import { isAllDayEvent } from '../components/calendar-view/utils';
import { useCalendarLocationIds } from '../stores/calendar-location.store';
import { useCalendarStore } from '../stores/calendar.store';
import { getTimeDiff } from '../utils/date-helpers';
import { CalendarTimeLineViewV2Props } from '../views/Calendar/CalendarTimeLineViewV2';
import { useGetCalendarV2QueriesData } from './use-get-calendar-v2-queries-data';

dayjs.extend(utc);
dayjs.extend(timezone);

const checkIsUnassignedProvider = (
  appointment: ReturnType<typeof useGetCalendarV2QueriesData>['appointments'][number],
  providerId: string,
  providerDisplayName: string
): boolean => {
  return (
    !appointment.practitionerName &&
    !!appointment.providerIds?.length &&
    appointment?.providerIds[0] === '' &&
    providerId === `unassigned-${appointment.locationId}` &&
    providerDisplayName === 'unassigned'
  );
};

const formatForSorting = (str: string) => str.replace(/[^a-zA-Z0-9\s]/g, ' ').toLowerCase();
const getProviderName = (provider: ScheduleTypes.SchemaProvider) => {
  return getFullName(provider) || provider.publicDisplayName?.trim() || provider.resourceName || '';
};

const sortProvidersByName = (providers: ScheduleTypes.SchemaProvider[]): ScheduleTypes.SchemaProvider[] => {
  // Separate unassigned providers from regular providers
  const regularProviders = providers.filter((provider) => provider.publicDisplayName !== 'unassigned');
  const unassignedProviders = providers.filter((provider) => provider.publicDisplayName === 'unassigned');

  // Sort only the regular providers alphabetically, then append unassigned providers at the end
  const sortedRegularProviders = regularProviders.sort((a, b) => {
    // Old Name used for sorting: a.publicDisplayName || a.lastName || a.firstName || a.resourceName || ''
    const aName = formatForSorting(getProviderName(a));
    const bName = formatForSorting(getProviderName(b));
    return aName.localeCompare(bName);
  });

  return [...sortedRegularProviders, ...unassignedProviders];
};

type CalendarGridV2Data = {
  headerType: 'provider';
  columns: ProviderCalendarColumn[];
  events: GridCalendarEvent[];
  allDayEvents: AllDayCalendarEvent[];
  columnGroups: CalendarColumnGroup[];
};

export const useCalendarGridV2Data = ({
  filteredAppointments,
  filteredProviderList,
  providerExceptions,
  providersOfficeHours,
  officeHours,
  officeExceptions,
}: CalendarTimeLineViewV2Props): CalendarGridV2Data => {
  const { calendarDate } = useCalendarStore(['calendarDate']);
  const calendarDateDay = dayjs(calendarDate)
    .format('dddd')
    .toLowerCase() as keyof ScheduleTypes.OfficeHoursWeekSchedule;
  const { calendarLocationIds } = useCalendarLocationIds();
  const { getScopeName } = useAppScopeStore();

  const sortedProviders = useMemo(() => sortProvidersByName(filteredProviderList), [filteredProviderList]);

  return useMemo(() => {
    const columns: ProviderCalendarColumn[] = [];
    const events: GridCalendarEvent[] = [];
    const allDayEvents: AllDayCalendarEvent[] = [];
    const addedGroupAllDayExceptionIds = new Set<string>(); // office exceptions are evaluated per-provider, dedupe to one group-scoped entry
    const columnGroups: CalendarColumnGroup[] = [];

    let groupOffset = 0;

    for (const locationId of calendarLocationIds) {
      const locationProviders = sortedProviders.filter((p) => p.locationId === locationId);

      const columnIds: string[] = [];

      for (const provider of locationProviders) {
        const columnId = provider.id ?? '';
        if (!columnId) continue;
        const columnEventIds: string[] = [];
        const providerDisplayName = provider.publicDisplayName?.trim() ?? '';
        const providerName = getProviderName(provider);

        //#region Office Breaks & Exceptions

        // Office Breaks
        const locationOfficeHours = officeHours?.find((office) => office.locationId === locationId)?.officeHours;
        const selectedDayOfficeHours = locationOfficeHours?.[calendarDateDay];
        selectedDayOfficeHours?.forEach((officeHour) => {
          const breaks = officeHour.breaks ?? [];
          breaks.forEach((breakItem) => {
            if (breakItem.startTime && breakItem.endTime) {
              const eventId = `break-${calendarDateDay}-${breakItem.startTime}-${breakItem.endTime}`;
              const startHour = breakItem.startTime;
              const endHour = breakItem.endTime;
              events.push({
                id: eventId,
                startTime: startHour,
                duration: getTimeDiff(startHour, endHour, { format: 'HH:mm' }),
                eventType: 'exception',
                // Enriched fields for GridExceptionCard
                title: 'Break',
                startDate: calendarDate,
                startHour,
                endHour,
                locationId: locationId,
                locationName: getScopeName(locationId ?? ''),
                entityId: columnId,
                entityName: providerName,
                calendarDate,
              } as GridCalendarEvent);
              columnEventIds.push(eventId);
            }
          });
        });

        // Office Exceptions
        const selectedLocationOfficeExceptions = officeExceptions.filter(
          (ex) => ex.locationId === locationId && !ex.isAvailable && !!ex.id
        );
        selectedLocationOfficeExceptions.forEach((ex) => {
          const startHour = dayjs(ex.startDateTime).format('HH:mm');
          const endHour = dayjs(ex.endDateTime).format('HH:mm');
          const exceptionId = ex.id ?? '';

          if (isAllDayEvent(startHour, endHour)) {
            if (!addedGroupAllDayExceptionIds.has(exceptionId)) {
              addedGroupAllDayExceptionIds.add(exceptionId);
              allDayEvents.push({
                id: exceptionId,
                name: ex.name?.trim() || 'Out of Office',
                scope: 'group',
                groupId: locationId,
              });
            }
            return;
          }

          events.push({
            id: exceptionId,
            eventType: 'exception',
            startTime: startHour,
            duration: getTimeDiff(startHour, endHour, { format: 'HH:mm' }),
            // Enriched fields for GridExceptionCard
            title: ex.name?.trim() || 'Out of Office',
            startDate: calendarDate,
            startHour,
            endHour,
            locationId: locationId,
            locationName: getScopeName(locationId ?? ''),
            entityId: columnId,
            entityName: providerName,
            calendarDate,
          } as GridCalendarEvent);
          columnEventIds.push(exceptionId);
        });

        //#endregion

        //#region Provider Breaks & Exceptions

        // Provider Breaks
        const providerOfficeHours = providersOfficeHours?.filter((p) => p.providerId === columnId);
        providerOfficeHours.forEach((providerOfficeHour) => {
          const breaks = providerOfficeHour.breaks ?? [];
          breaks.forEach((breakItem) => {
            if (breakItem.startTime && breakItem.endTime) {
              const eventId = `break-${providerOfficeHour.dayOfWeek}-${breakItem.startTime}-${breakItem.endTime}`;
              const startHour = breakItem.startTime;
              const endHour = breakItem.endTime;
              events.push({
                id: eventId,
                startTime: startHour,
                duration: getTimeDiff(startHour, endHour, { format: 'HH:mm' }),
                eventType: 'exception',
                // Enriched fields for GridExceptionCard
                title: 'Break',
                startDate: calendarDate,
                startHour,
                endHour,
                locationId: locationId,
                locationName: getScopeName(locationId ?? ''),
                entityId: columnId,
                entityName: providerName,
                calendarDate,
              } as GridCalendarEvent);
              columnEventIds.push(eventId);
            }
          });
        });

        // Provider Exceptions
        const selectedProviderExceptions = providerExceptions.filter(
          (ex) => ex.providerId === columnId && !ex.isAvailable && !!ex.id
        );
        selectedProviderExceptions.forEach((ex) => {
          const startHour = dayjs(ex.startDateTime).format('HH:mm');
          const endHour = dayjs(ex.endDateTime).format('HH:mm');
          const exceptionId = ex.id ?? '';

          if (isAllDayEvent(startHour, endHour)) {
            allDayEvents.push({
              id: exceptionId,
              name: ex.name?.trim() || 'Unavailable',
              scope: 'column',
              groupId: locationId,
              columnId,
            });
            return;
          }

          events.push({
            id: exceptionId,
            eventType: 'exception',
            startTime: startHour,
            duration: getTimeDiff(startHour, endHour, { format: 'HH:mm' }),
            // Enriched fields for GridExceptionCard
            title: ex.name?.trim() || 'Unavailable',
            startDate: calendarDate,
            startHour,
            endHour,
            locationId: locationId,
            locationName: getScopeName(locationId ?? ''),
            entityId: columnId,
            entityName: providerName,
            calendarDate,
          } as GridCalendarEvent);
          columnEventIds.push(exceptionId);
        });

        //#endregion

        const providerAppointments = filteredAppointments.filter((appt) => {
          const isUnassignedProvider = checkIsUnassignedProvider(appt, columnId, providerDisplayName);
          if (isUnassignedProvider) {
            return columnId === `unassigned-${locationId}` && providerDisplayName === 'unassigned';
          } else {
            return (
              (!!appt.providerIds?.length || providerName) &&
              appt.locationId === locationId &&
              appt.providerIds?.some((id) => {
                const eventPractitionerName = appt.practitionerName?.trim();
                if (id && columnId) {
                  return id === columnId;
                } else if (providerName && eventPractitionerName === providerName.trim()) {
                  return true;
                } else if (providerDisplayName && eventPractitionerName === providerDisplayName.trim()) {
                  return true;
                }
                return false;
              })
            );
          }
        });

        providerAppointments.forEach((appt) => {
          // TODO: ref old map function here: libs/features/schedule/src/hooks/use-get-appointments-events.ts
          const startHour = dayjs(appt.start).format('HH:mm');
          const endHour = appt.duration ? dayjs(appt.start).add(appt.duration, 'minute').format('HH:mm') : startHour;

          events.push({
            id: appt.id ?? '',
            startTime: startHour,
            duration: appt.duration ?? 0,
            eventType: 'appointment',
            // Enriched fields for GridAppointmentCard
            title: appt.person ? getFullName(appt.person) : 'Unknown',
            appointmentTypeName: appt.type,
            startDate: dayjs(appt.start).format('MM/DD/YYYY'),
            startHour,
            endHour,
            locationId: appt.locationId,
            locationName: getScopeName(appt.locationId ?? ''),
            entityId: columnId,
            entityName: providerName,
            personStatus: appt.person?.status,
            calendarDate,
            // V2 has no displayOptions — defaults apply in GridAppointmentCard
            eventTypeColor: undefined,
            backgroundColor: undefined,
            overlappingIndicatorVariant: undefined,
            isYesterdayEvent: false,
          } as GridCalendarEvent);
          columnEventIds.push(appt.id ?? '');
        });

        columns.push({
          id: columnId,
          name: providerName,
          providerImage: provider.publicDisplayImage || '',
          eventIds: columnEventIds,
          count: columnEventIds.length,
        });

        columnIds.push(columnId);
      }

      columnGroups.push({
        id: locationId,
        name: calendarLocationIds.length > 1 ? getScopeName(locationId) : '',
        columnIds,
        groupOffset,
      });

      groupOffset += columnIds.length;
    }

    return { headerType: 'provider' as const, columns, events, allDayEvents, columnGroups };
  }, [
    calendarLocationIds,
    sortedProviders,
    getScopeName,
    calendarDate,
    officeHours,
    calendarDateDay,
    officeExceptions,
    providersOfficeHours,
    providerExceptions,
    filteredAppointments,
  ]);
};
